import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Menu, X, Home, Users, Clock, UserCircle,
  DollarSign, CreditCard, Building2, Settings, Bell,
  Search, ChevronDown, BarChart2, Plug, Briefcase,
  CalendarDays, Lightbulb, ClipboardList, FileText,
  CheckSquare, ShieldCheck, TrendingUp, Sparkles,
  Mail, Phone, MapPin, ChevronRight, Download, Plus,
  Star, AlertCircle,
} from "lucide-react";
import styles from "./HrmsDemo.module.css";
import { cn } from "../../lib/utils";

// ─── Constants ────────────────────────────────────────────────────────────────

const COMPANY_ID = "64290";

const STATS = [
  { label: "Active Employees", value: 6,  icon: Users,        color: "#2563eb", bg: "#eff6ff" },
  { label: "New Approvals",    value: 2,  icon: ClipboardList, color: "#d97706", bg: "#fef3c7" },
  { label: "Leave Requests",   value: 1,  icon: CalendarDays,  color: "#7c3aed", bg: "#ede9fe" },
  { label: "Overtime (hrs)",   value: 1,  icon: Lightbulb,     color: "#059669", bg: "#d1fae5" },
];

const QUICK_LINKS = [
  { label: "Time off",   icon: CalendarDays, view: "leave"      },
  { label: "Payslip",    icon: FileText,     view: "payroll"    },
  { label: "Directory",  icon: Users,        view: "employees"  },
  { label: "My profile", icon: UserCircle,   view: "profile"    },
  { label: "Claims",     icon: ShieldCheck,  view: "finance"    },
  { label: "Tasks",      icon: CheckSquare,  view: null         },
];

const mockEmployees = [
  { id: "E-1042", name: "Dina Kusuma",    dept: "Engineering", role: "Senior Developer",   status: "Active",   joined: "2023-03-01" },
  { id: "E-1043", name: "Rizky Pratama",  dept: "People",      role: "HR Partner",         status: "Active",   joined: "2022-07-15" },
  { id: "E-1044", name: "Maya Putri",     dept: "Finance",     role: "Payroll Specialist", status: "Active",   joined: "2021-11-20" },
  { id: "E-1045", name: "Andi Wijaya",    dept: "Operations",  role: "Team Lead",          status: "On leave", joined: "2020-04-10" },
  { id: "E-1046", name: "Alex Martinez",  dept: "Engineering", role: "Frontend Developer", status: "Active",   joined: "2024-01-08" },
  { id: "E-1047", name: "Samantha Reed",  dept: "Operations",  role: "Project Manager",    status: "Active",   joined: "2023-09-01" },
];

const initialApprovals = [
  { id: "1", sender: "Alex Martinez",  avatar: "AM", avatarColor: "#4f46e5", date: "2026-03-24", description: "Time off • annual • Family trip (2026-03-24 → 2026-03-25)" },
  { id: "2", sender: "Samantha Reed",  avatar: "SR", avatarColor: "#0891b2", date: "2026-04-28", description: "Overtime • 4h • Release support" },
];

const celebrations = [
  { name: "Citra Lestari", event: "Birthday",         avatarColor: "#ec4899" },
  { name: "Marcus Chen",   event: "Work Anniversary", avatarColor: "#8b5cf6" },
  { name: "Olivia Smith",  event: "Birthday",         avatarColor: "#f59e0b" },
];

const MENU = [
  { key: "home",         label: "Home",             icon: Home,       view: "dashboard" },
  { key: "employees",    label: "Employees",         icon: Users,      submenu: [
    { label: "Employee directory", view: "employees" },
    { label: "Organization",       view: "company"   },
  ]},
  { key: "time",         label: "Time management",  icon: Clock,      submenu: [
    { label: "Attendance", view: "attendance" },
    { label: "Time off",   view: "leave"      },
  ]},
  { key: "profile",      label: "Employee Profile", icon: UserCircle, view: "profile"      },
  { key: "finance",      label: "Finance",          icon: DollarSign, submenu: [
    { label: "Benefit reimbursement", view: "finance" },
  ]},
  { key: "payroll",      label: "Payroll",          icon: CreditCard, view: "payroll"      },
  { key: "performance",  label: "Performance",      icon: BarChart2,  view: "performance"  },
  { key: "recruitment",  label: "Recruitment",      icon: Briefcase,  view: "recruitment"  },
  { key: "company",      label: "Company",          icon: Building2,  view: "company"      },
  { key: "integrations", label: "Integrations",     icon: Plug,       view: "integrations" },
  { key: "settings",     label: "Settings",         icon: Settings,   view: "settings"     },
];

const TITLE_MAP = {
  dashboard: "Home", employees: "Employees", attendance: "Attendance",
  leave: "Time off", profile: "Employee Profile", finance: "Finance",
  payroll: "Payroll", performance: "Performance", recruitment: "Recruitment",
  company: "Company", integrations: "Integrations", settings: "Settings",
};

// ─── AttendanceRing ───────────────────────────────────────────────────────────

function AttendanceRing({ clockedIn, time, date }) {
  const r = 66, cx = 90, cy = 90, circ = 2 * Math.PI * r;
  const dash = circ * (clockedIn ? 0.38 : 0);
  return (
    <div style={{ position: "relative", width: 180, height: 180, margin: "0 auto" }}>
      <svg width="180" height="180" viewBox="0 0 180 180" style={{ position: "absolute", top: 0, left: 0 }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth="9" />
        <circle cx={cx} cy={cy} r={r} fill="none"
          stroke={clockedIn ? "#2563eb" : "#cbd5e1"} strokeWidth="9"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition: "stroke-dasharray 0.4s" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: 24, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.04em", lineHeight: 1 }}>{time}</div>
        <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{date}</div>
      </div>
    </div>
  );
}

// ─── View: Employees ──────────────────────────────────────────────────────────

function EmployeesView() {
  const [q, setQ] = useState("");
  const list = mockEmployees.filter(e =>
    e.name.toLowerCase().includes(q.toLowerCase()) ||
    e.dept.toLowerCase().includes(q.toLowerCase())
  );
  return (
    <div className={styles.card}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className={styles.cardTitle}>Employee Directory</h3>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
            <input value={q} onChange={e => setQ(e.target.value)}
              className="border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:border-blue-400"
              placeholder="Search employees…" />
          </div>
          <button type="button" className={styles.btnPrimary}>
            <Plus className="h-3.5 w-3.5 mr-1" />Add Employee
          </button>
        </div>
      </div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead><tr><th>ID</th><th>Name</th><th>Department</th><th>Role</th><th>Joined</th><th>Status</th></tr></thead>
          <tbody>
            {list.map(e => (
              <tr key={e.id}>
                <td className="font-mono text-xs text-slate-400">{e.id}</td>
                <td>
                  <div className="flex items-center gap-2">
                    <div className={styles.miniAvatar} style={{ background: "#eff6ff", color: "#2563eb" }}>
                      {e.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                    </div>
                    <span className="font-medium text-slate-800">{e.name}</span>
                  </div>
                </td>
                <td className="text-slate-600">{e.dept}</td>
                <td className="text-slate-500 text-sm">{e.role}</td>
                <td className="text-slate-400 text-xs">{e.joined}</td>
                <td><span className={e.status === "Active" ? styles.badgeOk : styles.badgePending}>{e.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── View: Attendance ─────────────────────────────────────────────────────────

function AttendanceView() {
  const records = [
    { date: "2026-05-01", in: "08:02", out: "17:05", status: "Present" },
    { date: "2026-04-30", in: "08:15", out: "17:00", status: "Present" },
    { date: "2026-04-29", in: "08:00", out: "17:30", status: "Present" },
    { date: "2026-04-28", in: "—",     out: "—",     status: "Absent"  },
    { date: "2026-04-27", in: "08:10", out: "17:00", status: "Present" },
  ];
  return (
    <div className="flex flex-col gap-5">
      <div className={styles.card}>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className={styles.cardTitle}>Attendance — May 2026</h3>
          <button type="button" className={styles.btnPrimary}><Download className="h-3.5 w-3.5 mr-1" />Export</button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-3">
          {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => (
            <div key={d} className="text-center text-[11px] font-semibold text-slate-400 pb-1">{d}</div>
          ))}
          {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
            <div key={d} className={cn(
              "text-center text-xs py-1.5 rounded-lg font-medium cursor-default",
              d <= 2  ? "bg-green-50 text-green-700" :
              d === 11 ? "bg-blue-500 text-white" :
              "text-slate-500 hover:bg-slate-50"
            )}>{d}</div>
          ))}
        </div>
        <div className="flex gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded bg-green-200" />Present (2)</span>
          <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded bg-blue-500" />Today</span>
        </div>
      </div>
      <div className={styles.card}>
        <h3 className={styles.cardTitle} style={{ marginBottom: 16 }}>Punch Log</h3>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead><tr><th>Date</th><th>Clock In</th><th>Clock Out</th><th>Status</th></tr></thead>
            <tbody>
              {records.map(r => (
                <tr key={r.date}>
                  <td className="text-slate-600">{r.date}</td>
                  <td className="font-mono text-sm">{r.in}</td>
                  <td className="font-mono text-sm">{r.out}</td>
                  <td><span className={r.status === "Present" ? styles.badgeOk : styles.badgeAbsent}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── View: Leave ──────────────────────────────────────────────────────────────

function LeaveView() {
  const balances = [
    { type: "Annual Leave", used: 3, total: 12, color: "#2563eb" },
    { type: "Sick Leave",   used: 1, total: 5,  color: "#7c3aed" },
    { type: "Emergency",    used: 0, total: 3,  color: "#d97706" },
  ];
  const history = [
    { type: "Annual", dates: "Mar 24–25, 2026", days: 2, status: "Approved" },
    { type: "Sick",   dates: "Feb 10, 2026",    days: 1, status: "Approved" },
    { type: "Annual", dates: "Jan 3–4, 2026",   days: 2, status: "Approved" },
  ];
  return (
    <div className="flex flex-col gap-5">
      <div className={styles.card}>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className={styles.cardTitle}>Leave Balance</h3>
          <button type="button" className={styles.btnPrimary}><Plus className="h-3.5 w-3.5 mr-1" />Request Leave</button>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {balances.map(b => (
            <div key={b.type} className="rounded-xl border border-slate-100 p-4">
              <div className="text-xs font-semibold text-slate-500 mb-1">{b.type}</div>
              <div className="text-2xl font-bold" style={{ color: b.color }}>{b.total - b.used}</div>
              <div className="text-xs text-slate-400 mb-2">days remaining</div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${(b.used / b.total) * 100}%`, background: b.color }} />
              </div>
              <div className="text-[11px] text-slate-400 mt-1">{b.used} used of {b.total}</div>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.card}>
        <h3 className={styles.cardTitle} style={{ marginBottom: 16 }}>Leave History</h3>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead><tr><th>Type</th><th>Dates</th><th>Days</th><th>Status</th></tr></thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={i}>
                  <td className="font-medium text-slate-700">{h.type}</td>
                  <td className="text-slate-600 text-sm">{h.dates}</td>
                  <td className="text-slate-600">{h.days}</td>
                  <td><span className={styles.badgeOk}>{h.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── View: Profile ────────────────────────────────────────────────────────────

function ProfileView() {
  const [tab, setTab] = useState("personal");
  return (
    <div className={styles.card}>
      <div className="flex flex-col sm:flex-row gap-5 pb-5 border-b border-slate-100 mb-5">
        <div className={styles.profileLargeAvatar}>AD</div>
        <div>
          <h3 className={styles.cardTitle} style={{ marginBottom: 2 }}>Admin Demo</h3>
          <div className="text-sm text-slate-500 mb-3">HR Director · Information Technology</div>
          <div className="flex flex-wrap gap-4 text-sm text-slate-600">
            <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5 text-slate-400" />admin@company.com</span>
            <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5 text-slate-400" />+62 812-0000-0000</span>
            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-slate-400" />Kota Malang, Indonesia</span>
          </div>
        </div>
      </div>
      <div className="flex gap-1 mb-5">
        {["personal", "work", "documents"].map(t => (
          <button key={t} type="button"
            className={cn("px-4 py-2 rounded-lg text-sm font-medium transition capitalize", tab === t ? "bg-blue-50 text-blue-600" : "text-slate-500 hover:bg-slate-50")}
            onClick={() => setTab(t)}>{t === "personal" ? "Personal Info" : t === "work" ? "Work Info" : "Documents"}</button>
        ))}
      </div>
      {tab === "personal" && (
        <dl className="grid sm:grid-cols-2 gap-4 text-sm">
          {[["Full name","Admin Demo"],["Date of birth","Jan 01, 1990"],["Gender","—"],["Phone","+62 812-0000-0000"],["Email","admin@company.com"],["Address","Kota Malang, Indonesia"]].map(([k,v]) => (
            <div key={k}><dt className="text-slate-400 text-xs mb-0.5">{k}</dt><dd className="font-medium text-slate-800">{v}</dd></div>
          ))}
        </dl>
      )}
      {tab === "work" && (
        <dl className="grid sm:grid-cols-2 gap-4 text-sm">
          {[["Employee ID","E-1001"],["Department","Information Technology"],["Job title","HR Director"],["Manager","—"],["Start date","Jan 01, 2022"],["Work location","Kota Malang, Indonesia"]].map(([k,v]) => (
            <div key={k}><dt className="text-slate-400 text-xs mb-0.5">{k}</dt><dd className="font-medium text-slate-800">{v}</dd></div>
          ))}
        </dl>
      )}
      {tab === "documents" && (
        <div className="flex flex-col gap-2">
          {["Employment Contract.pdf","ID Card.pdf","Tax Form 2025.pdf"].map(f => (
            <div key={f} className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-3">
              <span className="flex items-center gap-2 text-sm font-medium text-slate-700"><FileText className="h-4 w-4 text-slate-400" />{f}</span>
              <button type="button" className={styles.linkBlue}><Download className="h-3.5 w-3.5 inline mr-1" />Download</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── View: Finance ────────────────────────────────────────────────────────────

function FinanceView() {
  const claims = [
    { id: "CLM-001", type: "Medical",        amount: "Rp 350.000", date: "2026-04-15", status: "Approved" },
    { id: "CLM-002", type: "Transport",       amount: "Rp 120.000", date: "2026-04-20", status: "Pending"  },
    { id: "CLM-003", type: "Meal Allowance",  amount: "Rp 75.000",  date: "2026-04-22", status: "Approved" },
  ];
  return (
    <div className={styles.card}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className={styles.cardTitle}>Benefit Reimbursement</h3>
        <button type="button" className={styles.btnPrimary}><Plus className="h-3.5 w-3.5 mr-1" />Submit Claim</button>
      </div>
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[{l:"Total Claimed",v:"Rp 545.000",c:"#2563eb"},{l:"Approved",v:"Rp 425.000",c:"#059669"},{l:"Pending",v:"Rp 120.000",c:"#d97706"}].map(s => (
          <div key={s.l} className="rounded-xl bg-slate-50 p-4">
            <div className="text-xs text-slate-500 mb-1">{s.l}</div>
            <div className="text-lg font-bold" style={{ color: s.c }}>{s.v}</div>
          </div>
        ))}
      </div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead><tr><th>Claim ID</th><th>Type</th><th>Amount</th><th>Date</th><th>Status</th></tr></thead>
          <tbody>
            {claims.map(c => (
              <tr key={c.id}>
                <td className="font-mono text-xs text-slate-400">{c.id}</td>
                <td className="font-medium text-slate-700">{c.type}</td>
                <td>{c.amount}</td>
                <td className="text-slate-400 text-xs">{c.date}</td>
                <td><span className={c.status === "Approved" ? styles.badgeOk : styles.badgePending}>{c.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── View: Payroll ────────────────────────────────────────────────────────────

function PayrollView() {
  const slips = [
    { period: "April 2026",    gross: "Rp 12.500.000", net: "Rp 10.850.000", status: "Paid" },
    { period: "March 2026",    gross: "Rp 12.500.000", net: "Rp 10.850.000", status: "Paid" },
    { period: "February 2026", gross: "Rp 12.500.000", net: "Rp 10.850.000", status: "Paid" },
  ];
  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-3 gap-4">
        {[{l:"Basic Salary",v:"Rp 10.000.000",icon:CreditCard,bg:"#eff6ff",c:"#2563eb"},{l:"Allowances",v:"Rp 2.500.000",icon:TrendingUp,bg:"#d1fae5",c:"#059669"},{l:"Deductions",v:"Rp 1.650.000",icon:AlertCircle,bg:"#fee2e2",c:"#dc2626"}].map(s => (
          <div key={s.l} className={styles.statCard}>
            <div className={styles.statIcon} style={{ background: s.bg, color: s.c }}><s.icon className="h-5 w-5" /></div>
            <div><div className={styles.statValue} style={{ fontSize: 17 }}>{s.v}</div><div className={styles.statLabel}>{s.l}</div></div>
          </div>
        ))}
      </div>
      <div className={styles.card}>
        <h3 className={styles.cardTitle} style={{ marginBottom: 16 }}>Payslip History</h3>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead><tr><th>Period</th><th>Gross Pay</th><th>Net Pay</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {slips.map(s => (
                <tr key={s.period}>
                  <td className="font-medium text-slate-700">{s.period}</td>
                  <td>{s.gross}</td>
                  <td className="font-semibold">{s.net}</td>
                  <td><span className={styles.badgeOk}>{s.status}</span></td>
                  <td><button type="button" className={styles.linkBlue}><Download className="h-3.5 w-3.5 inline mr-1" />Download</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── View: Performance ────────────────────────────────────────────────────────

function PerformanceView() {
  const reviews = [
    { name: "Dina Kusuma",   period: "Q1 2026", score: 92,   status: "Completed" },
    { name: "Rizky Pratama", period: "Q1 2026", score: 85,   status: "Completed" },
    { name: "Andi Wijaya",   period: "Q1 2026", score: null, status: "Pending"   },
  ];
  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-3 gap-4">
        {[{l:"Overall Score",v:"87/100",c:"#2563eb",bg:"#eff6ff"},{l:"Goals Met",v:"6/8",c:"#059669",bg:"#d1fae5"},{l:"Reviews Pending",v:"1",c:"#d97706",bg:"#fef3c7"}].map(s => (
          <div key={s.l} className={styles.statCard}>
            <div className={styles.statIcon} style={{ background: s.bg, color: s.c }}><Star className="h-5 w-5" /></div>
            <div><div className={styles.statValue}>{s.v}</div><div className={styles.statLabel}>{s.l}</div></div>
          </div>
        ))}
      </div>
      <div className={styles.card}>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className={styles.cardTitle}>Performance Reviews — Q1 2026</h3>
          <button type="button" className={styles.btnPrimary}><Plus className="h-3.5 w-3.5 mr-1" />Start Review</button>
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead><tr><th>Employee</th><th>Period</th><th>Score</th><th>Status</th></tr></thead>
            <tbody>
              {reviews.map(r => (
                <tr key={r.name}>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className={styles.miniAvatar} style={{ background: "#ede9fe", color: "#7c3aed" }}>
                        {r.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                      </div>
                      <span className="font-medium text-slate-800">{r.name}</span>
                    </div>
                  </td>
                  <td className="text-slate-600">{r.period}</td>
                  <td>{r.score != null ? <span className="font-bold text-slate-800">{r.score}</span> : <span className="text-slate-300">—</span>}</td>
                  <td><span className={r.status === "Completed" ? styles.badgeOk : styles.badgePending}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── View: Recruitment ────────────────────────────────────────────────────────

function RecruitmentView() {
  const jobs = [
    { title: "Backend Developer", dept: "Engineering", applicants: 12, status: "Active" },
    { title: "UI/UX Designer",    dept: "Product",     applicants: 8,  status: "Active" },
    { title: "Data Analyst",      dept: "Data",        applicants: 5,  status: "Closed" },
  ];
  return (
    <div className={styles.card}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className={styles.cardTitle}>Job Openings</h3>
        <button type="button" className={styles.btnPrimary}><Plus className="h-3.5 w-3.5 mr-1" />Post Job</button>
      </div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead><tr><th>Position</th><th>Department</th><th>Applicants</th><th>Status</th></tr></thead>
          <tbody>
            {jobs.map(j => (
              <tr key={j.title}>
                <td className="font-medium text-slate-800">{j.title}</td>
                <td className="text-slate-600">{j.dept}</td>
                <td><div className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-slate-400" /><span className="font-semibold">{j.applicants}</span></div></td>
                <td><span className={j.status === "Active" ? styles.badgeOk : styles.badgePending}>{j.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── View: Company ────────────────────────────────────────────────────────────

function CompanyView() {
  const depts = [
    { name: "Engineering",      head: "Dina Kusuma",   count: 2 },
    { name: "People & Culture", head: "Rizky Pratama", count: 1 },
    { name: "Finance",          head: "Maya Putri",    count: 1 },
    { name: "Operations",       head: "Andi Wijaya",   count: 2 },
  ];
  return (
    <div className="flex flex-col gap-5">
      <div className={styles.card}>
        <h3 className={styles.cardTitle} style={{ marginBottom: 16 }}>Company Overview</h3>
        <dl className="grid sm:grid-cols-2 gap-4 text-sm">
          {[["Company Name","Demo Corp"],["Industry","Technology"],["Founded","2018"],["Company Size","6–50"],["Headquarters","Kota Malang, Indonesia"],["Company ID", COMPANY_ID]].map(([k,v]) => (
            <div key={k}><dt className="text-slate-400 text-xs mb-0.5">{k}</dt><dd className="font-medium text-slate-800">{v}</dd></div>
          ))}
        </dl>
      </div>
      <div className={styles.card}>
        <h3 className={styles.cardTitle} style={{ marginBottom: 16 }}>Departments</h3>
        <div className="flex flex-col gap-2">
          {depts.map(d => (
            <div key={d.name} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3">
              <div>
                <div className="font-semibold text-sm text-slate-800">{d.name}</div>
                <div className="text-xs text-slate-400">Head: {d.head}</div>
              </div>
              <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-600">
                <Users className="h-3.5 w-3.5 text-slate-400" />{d.count}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── View: Integrations ───────────────────────────────────────────────────────

function IntegrationsView() {
  const [items, setItems] = useState([
    { name: "Slack",             desc: "Team notifications & reminders",   connected: true  },
    { name: "Google Workspace",  desc: "Calendar sync & directory",        connected: true  },
    { name: "Zoom",              desc: "Meeting & attendance integration",  connected: false },
    { name: "Jira",              desc: "Project & performance tracking",    connected: false },
  ]);
  const toggle = name => setItems(p => p.map(i => i.name === name ? { ...i, connected: !i.connected } : i));
  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle} style={{ marginBottom: 16 }}>Integrations</h3>
      <div className="flex flex-col gap-3">
        {items.map(i => (
          <div key={i.name} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3">
            <div className="flex items-center gap-3">
              <div className={styles.miniAvatar} style={{ width: 38, height: 38, fontSize: 14, background: "#f1f5f9", color: "#475569", borderRadius: 10 }}>
                {i.name[0]}
              </div>
              <div>
                <div className="font-semibold text-sm text-slate-800">{i.name}</div>
                <div className="text-xs text-slate-400">{i.desc}</div>
              </div>
            </div>
            <button type="button"
              className={i.connected ? styles.btnConnected : styles.btnPrimary}
              onClick={() => toggle(i.name)}>
              {i.connected ? "Connected" : "Connect"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── View: Settings ───────────────────────────────────────────────────────────

function SettingsView() {
  const sections = [
    { label: "General",           desc: "Company name, timezone, language"   },
    { label: "Access & Roles",    desc: "Manage permissions and user roles"  },
    { label: "Notifications",     desc: "Email and push notification settings" },
    { label: "Payroll Settings",  desc: "Pay cycle, tax, deductions"         },
    { label: "Leave Policies",    desc: "Leave types and approval workflow"  },
  ];
  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle} style={{ marginBottom: 16 }}>Settings Center</h3>
      <div className="flex flex-col gap-2">
        {sections.map(s => (
          <button key={s.label} type="button"
            className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3 text-left hover:border-blue-200 hover:bg-blue-50/40 transition w-full">
            <div>
              <div className="font-semibold text-sm text-slate-800">{s.label}</div>
              <div className="text-xs text-slate-400">{s.desc}</div>
            </div>
            <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function HrmsDemo() {
  const [mobileMenu, setMobileMenu]   = useState(false);
  const [activeView, setActiveView]   = useState("dashboard");
  const [expandedKey, setExpandedKey] = useState("employees");
  const [approvals, setApprovals]     = useState(initialApprovals);
  const [att, setAtt]                 = useState({ in: null, out: null });
  const [now, setNow]                 = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const setView = v => { setActiveView(v); setMobileMenu(false); };

  const timeStr  = now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  const secStr   = String(now.getSeconds()).padStart(2, "0");
  const dateStr  = now.toLocaleDateString("en-US", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const dateShort = now.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });

  const greeting = useMemo(() => {
    const h = now.getHours();
    return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  }, [now]);

  const isMenuActive = item =>
    item.view ? activeView === item.view :
    item.submenu ? item.submenu.some(s => s.view === activeView) : false;

  // ── Sidebar ────────────────────────────────────────────────────────────────
  const Sidebar = ({ onClose }) => (
    <aside className={styles.sidebar}>
      <div className={styles.logoRow}>
        <div className={styles.logoMark}>
          <svg viewBox="0 0 36 36" fill="none">
            <defs>
              <linearGradient id="lg1" x1="0" y1="0" x2="36" y2="36" gradientUnits="userSpaceOnUse">
                <stop stopColor="#f97316" /><stop offset="1" stopColor="#dc2626" />
              </linearGradient>
            </defs>
            <circle cx="18" cy="18" r="18" fill="url(#lg1)" />
            <circle cx="18" cy="18" r="7" stroke="white" strokeWidth="2" fill="none" />
            <path d="M18 11V18L22 22" stroke="white" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <span className={styles.logoText}>TIMELINE</span>
        {onClose && (
          <button type="button" className="ml-auto rounded p-1 hover:bg-slate-100 md:hidden" onClick={onClose}>
            <X className="h-5 w-5 text-slate-500" />
          </button>
        )}
      </div>

      <nav className={styles.nav}>
        {MENU.map(item => {
          const active = isMenuActive(item);
          if (!item.submenu) {
            return (
              <button key={item.key} type="button"
                className={cn(styles.navLink, active && styles.navActive)}
                onClick={() => setView(item.view)}>
                <item.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.8} />
                <span>{item.label}</span>
              </button>
            );
          }
          const open = expandedKey === item.key;
          return (
            <div key={item.key}>
              <button type="button"
                className={cn(styles.navLink, active && styles.navActive)}
                onClick={() => setExpandedKey(k => k === item.key ? null : item.key)}>
                <item.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.8} />
                <span className="flex-1 text-left">{item.label}</span>
                <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 transition-transform duration-200", open && "rotate-180")} strokeWidth={2} />
              </button>
              {open && (
                <div className={styles.submenu}>
                  {item.submenu.map(sub => (
                    <button key={sub.label} type="button"
                      className={cn(styles.submenuItem, activeView === sub.view && styles.submenuActive)}
                      onClick={() => setView(sub.view)}>
                      {sub.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className={styles.sidebarFooter}>Company ID : {COMPANY_ID}</div>
    </aside>
  );

  // ── Topbar ─────────────────────────────────────────────────────────────────
  const Topbar = () => (
    <div className={styles.topbar}>
      <button type="button" className="rounded-lg p-2 hover:bg-slate-100 md:hidden" onClick={() => setMobileMenu(true)}>
        <Menu className="h-5 w-5 text-slate-600" />
      </button>
      <button type="button" className={styles.summarizeBtn}>
        <Sparkles className="h-3.5 w-3.5" />
        <span>Summarize data</span>
      </button>
      <div className="flex-1" />
      <div className={styles.topbarRight}>
        <button type="button" className={styles.iconBtnBadge} aria-label="Notifications">
          <Bell className="h-[18px] w-[18px]" />
          <span className={styles.notifBadge}>3</span>
        </button>
        <button type="button" className={styles.iconBtn2} aria-label="Search"><Search className="h-[18px] w-[18px]" /></button>
        <button type="button" className={styles.iconBtn2} aria-label="Settings"><Settings className="h-[18px] w-[18px]" /></button>
        <button type="button" className={styles.iconBtn2} aria-label="Reports"><BarChart2 className="h-[18px] w-[18px]" /></button>
        <div className={styles.tbDivider} />
        <button type="button" className={styles.profileBtn}>
          <div className={styles.profileAvatar}>JC</div>
          <div className={styles.profileInfo}>
            <span className={styles.profileName}>Jane Cooper</span>
            <span className={styles.profileRole}>HR Director</span>
          </div>
          <ChevronDown className="h-4 w-4 text-slate-400" strokeWidth={2} />
        </button>
      </div>
    </div>
  );

  // ── Dashboard ──────────────────────────────────────────────────────────────
  const Dashboard = () => (
    <div>
      {/* Hero */}
      <section className={styles.hero}>
        <div>
          <p className={styles.heroDate}>{dateStr}</p>
          <h1 className={styles.heroTitle}>{greeting}, Admin Demo</h1>
          <p className={styles.heroSub}>Manage your company with ease and efficiency.</p>
        </div>
        <div className={styles.clockBox}>
          <div className={styles.clockBig}>{timeStr}</div>
          <div className={styles.clockSec}>{secStr}</div>
        </div>
      </section>

      {/* Stats */}
      <div className={styles.statsRow}>
        {STATS.map(s => (
          <div key={s.label} className={styles.statCard}>
            <div className={styles.statIcon} style={{ background: s.bg, color: s.color }}>
              <s.icon className="h-5 w-5" strokeWidth={2} />
            </div>
            <div>
              <div className={styles.statValue}>{s.value}</div>
              <div className={styles.statLabel}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className={styles.dashGrid}>
        {/* Left */}
        <div className="flex flex-col gap-5">
          {/* Pending Approvals */}
          <div className={styles.card}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h3 className={styles.cardTitle}>Pending Approvals</h3>
                {approvals.length > 0 && <span className={styles.approvalBadge}>{approvals.length}</span>}
              </div>
              <button type="button" className={styles.linkBlue}>View all</button>
            </div>
            {approvals.length === 0
              ? <p className="text-sm text-slate-400">No pending approvals.</p>
              : approvals.map(row => (
                <div key={row.id} className={styles.approvalRow}>
                  <div className={styles.approvalAvatar} style={{ background: row.avatarColor }}>{row.avatar}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="text-sm font-semibold text-slate-800">{row.sender}</span>
                      <span className="text-xs text-slate-400">{row.date}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{row.description}</p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button type="button" className={styles.btnApprove} onClick={() => setApprovals(p => p.filter(x => x.id !== row.id))}>Approve</button>
                    <button type="button" className={styles.btnReject}  onClick={() => setApprovals(p => p.filter(x => x.id !== row.id))}>Reject</button>
                  </div>
                </div>
              ))
            }
          </div>

          {/* Announcement */}
          <div className={styles.card}>
            <h3 className={styles.cardTitle} style={{ marginBottom: 14 }}>Announcement</h3>
            <div className={styles.announceBanner}>
              <div className="relative z-[1]">
                <span className={styles.announceEyebrow}>Company Event</span>
                <h4 className={styles.announceTitle}>Townhall Meeting Q1 2026</h4>
                <p className={styles.announceBody}>Join our quarterly townhall to discuss our roadmap and goals for 2026.</p>
                <button type="button" className={styles.announceBtn}>Read More</button>
              </div>
              <div className={styles.announceDeco} aria-hidden>
                <svg viewBox="0 0 200 120" fill="none" className="w-full h-full">
                  {[40,70,100,130,160].map((x, i) => (
                    <g key={x} opacity={0.55 - i * 0.06}>
                      <circle cx={x} cy={45 + (i % 2) * 8} r={12 + i * 1.5} fill="white" />
                      <rect x={x - 16} y={60 + (i % 2) * 8} width={32 + i * 2} height={48} rx="14" fill="white" />
                    </g>
                  ))}
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Right */}
        <div className="flex flex-col gap-5">
          {/* Attendance */}
          <div className={styles.card}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={styles.cardTitle}>Attendance</h3>
              <span className={styles.wibBadge}>WIB</span>
            </div>
            <AttendanceRing clockedIn={!!att.in && !att.out} time={timeStr} date={dateShort} />
            <p className={styles.attStatus}>
              {att.in && !att.out ? `Checked in ${att.in} · awaiting clock out`
                : att.in && att.out ? `Done: ${att.in} – ${att.out}`
                : "Not checked in today."}
            </p>
            <div className={styles.attBtns}>
              <button type="button" className={styles.btnClockIn} disabled={!!att.in}
                onClick={() => setAtt(a => ({ ...a, in: timeStr }))}>Clock In</button>
              <button type="button" className={styles.btnClockOut} disabled={!att.in || !!att.out}
                onClick={() => setAtt(a => ({ ...a, out: timeStr }))}>Clock Out</button>
            </div>
            <div className={styles.attMeta}>
              <span>🕒 08:00 - 17:00</span>
              <button type="button" className={styles.linkBlue}>Attendance log</button>
            </div>
          </div>

          {/* Quick Links */}
          <div className={styles.card}>
            <h3 className={styles.cardTitle} style={{ marginBottom: 12 }}>Quick Links</h3>
            <div className={styles.quickGrid}>
              {QUICK_LINKS.map(q => (
                <button key={q.label} type="button" className={styles.quickItem}
                  onClick={() => q.view && setView(q.view)}>
                  <q.icon className="h-4 w-4 shrink-0" strokeWidth={1.8} />
                  <span>{q.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Celebrations */}
          <div className={styles.card}>
            <h3 className={styles.cardTitle} style={{ marginBottom: 12 }}>Celebrations</h3>
            <div className="flex flex-col gap-3">
              {celebrations.map(c => (
                <div key={c.name} className={styles.celebRow}>
                  <div className={styles.celebAvatar} style={{ background: c.avatarColor }}>
                    {c.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-800">{c.name}</div>
                    <div className="text-xs text-slate-400">{c.event}</div>
                  </div>
                  <button type="button" className={styles.btnWish}>Send Wish</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Render view ────────────────────────────────────────────────────────────
  const renderView = () => {
    switch (activeView) {
      case "dashboard":    return <Dashboard />;
      case "employees":    return <EmployeesView />;
      case "attendance":   return <AttendanceView />;
      case "leave":        return <LeaveView />;
      case "profile":      return <ProfileView />;
      case "finance":      return <FinanceView />;
      case "payroll":      return <PayrollView />;
      case "performance":  return <PerformanceView />;
      case "recruitment":  return <RecruitmentView />;
      case "company":      return <CompanyView />;
      case "integrations": return <IntegrationsView />;
      case "settings":     return <SettingsView />;
      default:             return null;
    }
  };

  // ── Root render ────────────────────────────────────────────────────────────
  return (
    <div className="flex min-h-screen flex-col bg-[#f1f5f9]">
      <header className="z-[70] flex shrink-0 items-center gap-3 border-b border-slate-800 bg-slate-950 px-4 py-2.5">
        <Link to="/#projects" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white">
          <ArrowLeft className="h-4 w-4" />Back to portfolio
        </Link>
        <span className="hidden text-slate-600 sm:inline">|</span>
        <span className="text-xs text-slate-500">Timeline HRIS — interactive UI demo</span>
      </header>

      <div className={cn(styles.page, "flex min-h-0 flex-1")}>
        {mobileMenu && (
          <button type="button" className="fixed inset-0 z-[55] bg-black/40 md:hidden" onClick={() => setMobileMenu(false)} />
        )}
        <div className={cn("fixed inset-y-0 left-0 z-[60] md:static md:z-auto transition-transform duration-200",
          mobileMenu ? "translate-x-0" : "-translate-x-full md:translate-x-0")}>
          <div className="relative h-full shadow-xl md:shadow-none">
            <Sidebar onClose={() => setMobileMenu(false)} />
          </div>
        </div>

        <div className={styles.content}>
          <Topbar />
          <div className={styles.mainPad}>
            <p className="mb-4 text-xs text-slate-400">
              Home{activeView !== "dashboard" && <><span className="mx-1">/</span><span className="font-medium text-slate-600">{TITLE_MAP[activeView]}</span></>}
            </p>
            {renderView()}
          </div>
        </div>
      </div>
    </div>
  );
}
